@let load = loading();
<!-- 1. Loading State - Only on initial load when there are no results -->
@if (load && results().length === 0) {
	<!-- Show full placeholder only on initial load -->
	<div class="text-center py-8">
		<icon
			[icon]="'fas fa-spinner fa-spin'"
			class="text-blue-500 text-2xl"
		/>
		<p class="text-gray-500 mt-2">{{ 'components.globalSearch.loading' | translate }}</p>
	</div>
}

<!-- 2. Error State -->
@if (error()) {
	<div class="text-center py-8 bg-red-50 text-red-700 p-4 rounded border border-red-200">
		<icon
			[icon]="'fas fa-exclamation-triangle'"
			class="text-xl"
		/>
		<p class="mt-2 font-semibold">{{ 'components.globalSearch.errorOccurred' | translate }}</p>
		<p class="">{{ 'components.globalSearch.loadError' | translate }}</p>
	</div>
}

<!-- 3. No Results State -->
@if (showNoResultsMessage()) {
	<div class="text-center py-8 text-gray-500 italic">
		{{ 'components.globalSearch.noResultsFound' | translate }}
		{{
			category() === GlobalSearchCategory.CYCLE
				? ('components.globalSearch.documents' | translate)
				: category() === GlobalSearchCategory.USER
					? ('components.globalSearch.users' | translate)
					: ('components.globalSearch.groups' | translate)
		}}
		{{ 'components.globalSearch.matchingSearch' | translate }} "{{ searchTerm() }}".
	</div>
}

<!-- Sort controls - Show only when we have results -->
@if (results().length > 0) {
	<div class="sort-controls">
		<span class="text-sm text-gray-600">{{ 'components.globalSearch.sortBy' | translate }}</span>
		<div class="sort-dropdown">
			<p-dropdown
				[options]="sortOptions() | translateArray: 'label'"
				optionLabel="label"
				optionValue="value"
				[style]="{'min-width': '150px'}"
				[ngModel]="sortBy()"
				(ngModelChange)="onSortByChange($event)"
				appendTo="body"
				styleClass="sort-dropdown dense-min"
				[overlayOptions]="{styleClass: 'global-search-sort-dropdown dense-min', hideOnEscape: true}"
			/>
		</div>
		<button
			type="button"
			class="sort-direction-button"
			[class.asc]="sortDirection() === 'asc'"
			[class.desc]="sortDirection() === 'desc'"
			(click)="onSortDirectionChange(sortDirection() === 'asc' ? 'desc' : 'asc')"
			[attr.title]="'components.globalSearch.sortDirection.' + (sortDirection() === 'asc' ? 'desc' : 'asc') | translate"
			[pTooltip]="'components.globalSearch.sortDirection.' + (sortDirection() === 'asc' ? 'desc' : 'asc') | translate"
			tooltipStyleClass="dc-tooltip dc-gray-tooltip"
			[tooltipOptions]="{positionTop: -5}"
			tooltipPosition="top"
		>
			@let sortDirectionIcon = sortDirection() === 'asc' ? 'fas fa-sort-amount-up' : 'fas fa-sort-amount-down';
			<icon [icon]="sortDirectionIcon" />
		</button>
	</div>
}

<!-- 4. Display Results -->
<div class="bottom-section-container">
	@if (results().length > 0) {
		<div class="results-list">
			@for (item of results(); track trackByResultItem($index, item); let isLast = $last) {
				@switch (category()) {
					@case (GlobalSearchCategory.CYCLE) {
						<dc-cycle-result-item
							[item]="castToCycle(item)"
							[id]="isLast && isNewlyLoaded() ? 'last-result-item' : ''"
						/>
					}
					@case (GlobalSearchCategory.USER) {
						<dc-user-result-item
							[item]="castToUser(item)"
							[id]="isLast && isNewlyLoaded() ? 'last-result-item' : ''"
						/>
					}
					@case (GlobalSearchCategory.USER_GROUP) {
						<dc-usergroup-result-item
							[item]="castToUserGroup(item)"
							[id]="isLast && isNewlyLoaded() ? 'last-result-item' : ''"
						/>
					}
					@default {
						<p
							class="text-red-500"
							[id]="isLast && isNewlyLoaded() ? 'last-result-item' : ''"
						>
							{{ 'components.globalSearch.unknownCategory' | translate }}
						</p>
					}
				}
			}
		</div>
	}

	<div class="footer">
		<!-- Loading more indicator when button is clicked -->
		@if (load && results().length > 0) {
			<div class="footer-section">
				<icon
					[icon]="'fas fa-spinner fa-spin'"
					class="text-blue-500 text-xl"
				/>
				<p class="text-gray-500 mt-1">{{ 'components.globalSearch.loadingMoreResults' | translate }}</p>
			</div>
		}

		<!-- 5. Load More Button -->
		@if (showLoadMoreButton() && results().length < total()) {
			<div class="footer-section justify-between">
				<div class="flex items-center gap-2">
					<button
						type="button"
						class="flex items-center justify-center px-4 py-1.5 bg-white border border-gray-200 rounded shadow-sm hover:bg-gray-50"
						(click)="onLoadMoreClick()"
						[disabled]="load"
					>
						@if (load) {
							<div class="loading-container">
								<icon
									[icon]="'fas fa-spinner fa-spin'"
									class="me-2 text-blue-500"
								/>
								{{ 'components.globalSearch.loadingEllipsis' | translate }}
							</div>
						} @else {
							<icon
								[icon]="'fas fa-plus-circle'"
								class="me-2 text-blue-500"
							/>
							{{ 'components.globalSearch.loadMoreResults' | translate }}
						}
					</button>
					<span class="text-xs font-medium bg-gray-100 px-2 py-0.5 rounded-full">({{ results().length }} / {{ total() }} )</span>
				</div>

				<div class="flex items-center gap-2 shrink-0">
					<span class="text-xs text-gray-600">{{ 'components.globalSearch.resultsPerPage' | translate }}:</span>
					<div class="rows-per-page-select-container">
						@let rowsPerPageOptions =
							[
								{label: '5', value: 5},
								{label: '10', value: 10},
								{label: '20', value: 20},
								{label: '50', value: 50},
								{label: '100', value: 100},
							];
						<p-dropdown
							[options]="rowsPerPageOptions"
							optionLabel="label"
							optionValue="value"
							appendTo="body"
							size="small"
							[ngModel]="pageSize()"
							(ngModelChange)="onPageSizeChange($event)"
							[disabled]="load"
							class="rows-per-page-select dense-min"
							styleClass="h-full
"
						/>
					</div>
				</div>
			</div>
		}
	</div>
</div>
